/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */


/* =================================================================
 * Quick add-to-cart "+" button with variant popup
 *  - 1 variant     → POST /cart/add.js directly
 *  - 2+ variants   → open popup, user picks variant, then POST
 *  - On success    → refresh & open the cart drawer
 * Vanilla JS, event delegation. Hooks: .upsell-atc-btn, .variant-popup,
 *                                       .variant-options li, .variant-popup-close
 * ================================================================= */
(function () {
  function addToCart(variantId) {
    if (!variantId) return;

    // If the click came from inside an already-open cart drawer (e.g. a "+"
    // on a cross-sell card), the upcoming HTML merge will replace that "+"
    // button and focus drops to <body>. cart-form.refreshFromHtml has a
    // recovery that pulls focus to the drawer close button in that case,
    // which makes VoiceOver announce "Cerrar, diálogo" before the real
    // "Just added to cart, Product X" message. Set the post-merge guard
    // flag so the recovery skips that hop — onMerged below will land focus
    // on the just-added line item directly.
    if (typeof theme !== 'undefined' && document.querySelector('cart-drawer[open]')) {
      theme._pendingPostMergeFocus = true;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        // Schedule a screen-reader announcement once the cart drawer has
        // refreshed (so the subtotal in the DOM is current). The drawer opens
        // first; we delay the announcement so VoiceOver can finish reading
        // the dialog title ("Carrito") before the live region speaks.
        // The a11y confirmation is delivered by moving keyboard focus to the
        // newly-added cart item (which carries a hidden "Just added to cart"
        // prefix in its accessible name). See theme.focusJustAddedCartItem.
        var pendingFocusItem = null;
        var drawerWasOpen = !!document.querySelector('cart-drawer[open]');
        if (data && data.key && typeof theme !== 'undefined' && typeof theme.focusJustAddedCartItem === 'function') {
          var onMerged = function () {
            document.removeEventListener('on:cart:after-merge', onMerged);
            theme._pendingPostMergeFocus = false;
            var itemEl = theme.focusJustAddedCartItem(data.key);
            if (drawerWasOpen && itemEl) {
              // Drawer is already open; the upcoming open() dispatch will
              // no-op, so move focus to the new item directly.
              itemEl.focus();
            } else if (itemEl) {
              // Drawer was closed (collection card or product-page suggestion).
              // dispatch:cart-drawer:open below already fired with
              // pendingFocusItem null, so the drawer landed focus on its
              // default target. Take over now and move focus to the just-added
              // line item so VoiceOver announces "Producto X añadido al
              // carrito". Defer one frame so the drawer's [open] visibility
              // styles have applied — focusing while still
              // visibility:hidden silently drops focus to <body>.
              requestAnimationFrame(function () {
                if (itemEl.isConnected) itemEl.focus();
              });
              // Stash too in case the dispatch:open dispatch also tries to
              // pick a target later (defensive; harmless if unused).
              pendingFocusItem = itemEl;
            }
          };
          document.addEventListener('on:cart:after-merge', onMerged, { once: true });
        } else if (typeof theme !== 'undefined') {
          // No after-merge focus path will run (e.g. response had no key),
          // so don't leave the guard stuck on.
          theme._pendingPostMergeFocus = false;
        }

        // If the search popup is open, close it first and capture its opener
        // (typically the search icon in the header) so the cart drawer can
        // restore focus there on close — otherwise focus is lost in a layer
        // that is no longer visible.
        var cartOpener = null;
        if (document.body.classList.contains('show-search')) {
          cartOpener = (typeof theme !== 'undefined' && theme.trapOpener)
            || document.querySelector('.show-search-link');
          var searchCloseBtn = document.querySelector('.main-search .main-search__close');
          if (searchCloseBtn) searchCloseBtn.click();
        }

        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
          bubbles: true,
          detail: { opener: cartOpener || undefined, elementToFocus: pendingFocusItem || undefined }
        }));
      })
      .catch(function (err) { console.error('[upsell-atc] add to cart failed:', err); });
  }

  // Track which "+" button opened the current popup, so focus returns there on close.
  var popupOpener = null;

  function focusableOptions(popup) {
    return Array.prototype.slice.call(
      popup.querySelectorAll('.variant-options li:not(.disabled)')
    );
  }

  function closePopup(popup, returnFocus) {
    if (!popup) return;
    popup.classList.remove('active');
    if (returnFocus !== false && popupOpener) {
      try { popupOpener.focus(); } catch (_) { /* no-op */ }
    }
    popupOpener = null;
  }

  function closeAllPopups(returnFocus) {
    document.querySelectorAll('.variant-popup.active').forEach(function (p) {
      closePopup(p, returnFocus);
    });
  }

  // Keyboard handling inside an open popup: Escape, ArrowUp/Down, Home/End, Enter/Space.
  document.addEventListener('keydown', function (e) {
    var popup = document.querySelector('.variant-popup.active');
    if (!popup || !popup.contains(e.target)) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      closePopup(popup);
      return;
    }

    var options = focusableOptions(popup);
    if (!options.length) return;
    var currentIndex = options.indexOf(e.target);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      var next = currentIndex < 0 ? 0 : Math.min(currentIndex + 1, options.length - 1);
      options[next].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      var prev = currentIndex < 0 ? 0 : Math.max(currentIndex - 1, 0);
      options[prev].focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      options[0].focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      options[options.length - 1].focus();
    } else if ((e.key === 'Enter' || e.key === ' ') && currentIndex >= 0) {
      e.preventDefault();
      options[currentIndex].click();
    }
  });

  document.addEventListener('click', function (e) {
    // 1. "+" button on product card
    var btn = e.target.closest('.upsell-atc-btn');
    if (btn) {
      e.preventDefault();
      var variants;
      try { variants = JSON.parse(btn.dataset.variants || '[]'); }
      catch (_) { variants = []; }

      if (!variants || variants.length <= 1) {
        addToCart(btn.dataset.id);
        return;
      }

      var block = btn.closest('.product-block__detail-cart-t');
      if (!block) { addToCart(btn.dataset.id); return; }
      var popup = block.querySelector('.variant-popup');
      var options = popup && popup.querySelector('.variant-options');
      if (!popup || !options) { addToCart(btn.dataset.id); return; }

      options.innerHTML = '';
      variants.forEach(function (v) {
        var li = document.createElement('li');
        li.dataset.id = v.id;
        li.setAttribute('role', 'option');
        if (v.available) {
          li.textContent = v.title;
          li.setAttribute('tabindex', '0');
          li.setAttribute('aria-label', v.title);
        } else {
          li.classList.add('disabled');
          li.setAttribute('aria-disabled', 'true');
          li.textContent = v.title + ' (Sold out)';
        }
        options.appendChild(li);
      });

      closeAllPopups(false);
      popupOpener = btn;
      popup.classList.add('active');

      // Move focus to the first selectable option for immediate keyboard control.
      var firstSelectable = focusableOptions(popup)[0]
        || popup.querySelector('.variant-popup-close');
      if (firstSelectable) {
        // requestAnimationFrame so the popup is rendered before focusing.
        requestAnimationFrame(function () { firstSelectable.focus(); });
      }
      return;
    }

    // 2. Close button inside popup → return focus to the opener.
    if (e.target.closest('.variant-popup-close')) {
      var p = e.target.closest('.variant-popup');
      closePopup(p);
      return;
    }

    // 3. Variant option chosen → don't return focus (cart drawer will open).
    var li = e.target.closest('.variant-options li');
    if (li) {
      if (li.classList.contains('disabled')) return;
      var popupParent = li.closest('.variant-popup');
      closePopup(popupParent, false);
      addToCart(li.dataset.id);
      return;
    }

    // 4. Click outside any popup → close all (no focus restore for mouse clicks).
    if (!e.target.closest('.variant-popup')) {
      closeAllPopups(false);
    }
  });
})();


/* =================================================================
 * A11y: close the collection filter panel on Escape key
 *  - Theme toggles .filter-container--show-filters-desktop / -mobile
 *    on a <filter-container> when the user clicks [data-toggle-filters]
 *  - Pressing Esc should do the same dismiss action (mobile + desktop)
 * ================================================================= */
document.addEventListener('keydown', function (e) {
  if (e.key !== 'Escape') return;
  var openContainer = document.querySelector(
    'filter-container.filter-container--show-filters-mobile, filter-container.filter-container--show-filters-desktop'
  );
  if (!openContainer) return;

  // Reuse the theme's existing toggle: triggering a click on a
  // [data-toggle-filters] button keeps the toggle state in sync with the
  // theme's internal flags (toggle-btn--revealed-desktop class, etc.).
  var toggleBtn = openContainer.querySelector('[data-toggle-filters]');
  if (toggleBtn) {
    toggleBtn.click();
  } else {
    openContainer.classList.remove(
      'filter-container--show-filters-mobile',
      'filter-container--show-filters-desktop'
    );
  }
});


/* =================================================================
 * A11y: filter panel — focus management + manual mobile focus trap
 *
 * Uses a MutationObserver on <filter-container> to react to class
 * changes (filter-container--show-filters-mobile / -desktop). More
 * robust than relying on the click handler order with the theme.
 *
 *  - On open: move focus into the panel (so Tab doesn't walk through
 *    sibling utility-bar icons first).
 *  - On mobile only: trap Tab inside the panel via a keydown listener
 *    that recomputes the focusable list at Tab time. Desktop keeps
 *    natural tab order — the sidebar is always visible there.
 *  - On close: restore focus to the toggle that opened it.
 * ================================================================= */
(function () {
  var FOCUSABLE_SEL =
    'a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), ' +
    'select:not([disabled]), textarea:not([disabled]), summary, ' +
    '[tabindex]:not([tabindex^="-"])';

  var trappedPanel = null;
  var filterTrapOpener = null;
  var lastToggleClicked = null;

  function getVisibleFocusables(panel) {
    return Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE_SEL))
      .filter(function (el) {
        return el.offsetParent !== null && getComputedStyle(el).visibility !== 'hidden';
      });
  }

  // Manual focus trap on Tab — only active when trappedPanel is set
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' || !trappedPanel) return;
    var focusables = getVisibleFocusables(trappedPanel);
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    var active = document.activeElement;
    // If focus has somehow escaped the panel, pull it back.
    if (!trappedPanel.contains(active)) {
      e.preventDefault();
      first.focus();
      return;
    }
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  });

  // Track which toggle was clicked so we can return focus to it on close
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-toggle-filters]');
    if (btn) lastToggleClicked = btn;
  });

  function handleStateChange(filterContainer) {
    var openMobile = filterContainer.classList.contains('filter-container--show-filters-mobile');
    var openDesktop = filterContainer.classList.contains('filter-container--show-filters-desktop');
    var panel = filterContainer.querySelector('.filters');

    if (openMobile || openDesktop) {
      // Just opened
      if (panel) {
        var firstFocusable = panel.querySelector('.filters__close') || panel.querySelector(FOCUSABLE_SEL);
        if (firstFocusable && !panel.contains(document.activeElement)) {
          firstFocusable.focus();
        }
      }
      // Trap only on mobile
      if (openMobile && panel) {
        trappedPanel = panel;
        if (!filterTrapOpener) filterTrapOpener = lastToggleClicked;
      } else {
        trappedPanel = null;
      }
    } else {
      // Just closed
      trappedPanel = null;
      if (filterTrapOpener) {
        filterTrapOpener.focus();
        filterTrapOpener = null;
      }
    }
  }

  function attachObservers() {
    document.querySelectorAll('filter-container').forEach(function (fc) {
      if (fc.dataset.a11yObserverAttached === '1') return;
      fc.dataset.a11yObserverAttached = '1';
      var obs = new MutationObserver(function () {
        handleStateChange(fc);
      });
      obs.observe(fc, { attributes: true, attributeFilter: ['class'] });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachObservers);
  } else {
    attachObservers();
  }
})();


/* =================================================================
 * A11y: close the mobile hamburger menu on Escape key
 *  - Theme toggles body.reveal-mobile-nav when user clicks .mobile-nav-toggle
 *  - Pressing Esc should trigger the same dismiss path so the focus
 *    trap and transition flags get cleaned up correctly
 * ================================================================= */
document.addEventListener('keydown', function (e) {
  if (e.key !== 'Escape') return;
  if (!document.body.classList.contains('reveal-mobile-nav')) return;

  // Click the toggle button so the theme runs its full close flow
  // (removes reveal-mobile-nav, --revealed, enable-mobile-nav-transition,
  // releases the focus trap).
  var toggle = document.querySelector('.mobile-navigation-drawer .mobile-nav-toggle')
            || document.querySelector('.mobile-nav-toggle');
  if (toggle) toggle.click();
});


